import { Injectable, inject } from '@angular/core';
import { buildPermissionClaim, normalizePermissionAction } from '../utils/permission-action.utils';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root'
})
export class PermissionService {
    private authService = inject(AuthService);
    private static readonly USER_KEYWORDS = ['user', 'users', 'usuario', 'usuarios'];
    private static readonly USER_READ_ACTIONS = ['read', 'get', 'list', 'view', '*'];
    private static readonly USER_WRITE_ACTIONS = ['create', 'post', 'put', 'patch', 'update', 'delete', 'write', '*'];

    /**
     * Checks if the current user has a specific permission
     * @param permission The permission string to check
     * @returns boolean
     */
    hasPermission(permission: string): boolean {
        if (this.authService.isSuperUser()) {
            return true;
        }
        const user = this.authService.currentUser();
        if (!user || !user.permissions) {
            return false;
        }
        return user.permissions.some((grantedPermission) => this.matchesPermission(grantedPermission, permission));
    }

    /**
     * Checks if the current user has access to a specific module/section/page
     * Logic: Access is granted if the user has 'read' permission for that resource
     * Pattern: permission:[module]:[section]:[page]:read
     */
    canAccess(module: string, section: string, page: string): boolean {
        return this.hasPermission(buildPermissionClaim(module, section, page, 'read'));
    }

    canViewUsers(): boolean {
        if (this.authService.isRbiAdmin() || this.authService.isSuperUser()) {
            return true;
        }

        return this.hasPermissionByKeywords(
            PermissionService.USER_KEYWORDS,
            PermissionService.USER_READ_ACTIONS,
        ) || this.canManageUsers();
    }

    canManageUsers(): boolean {
        if (this.authService.isRbiAdmin() || this.authService.isSuperUser()) {
            return true;
        }

        return this.hasPermissionByKeywords(
            PermissionService.USER_KEYWORDS,
            PermissionService.USER_WRITE_ACTIONS,
        );
    }

    private matchesPermission(grantedPermission: string, requestedPermission: string): boolean {
        if (grantedPermission === requestedPermission) {
            return true;
        }

        const grantedParts = grantedPermission.split(':');
        const requestedParts = requestedPermission.split(':');
        if (grantedParts.length !== requestedParts.length) {
            return false;
        }

        return grantedParts.every((part, index) => part === '*' || part === requestedParts[index]);
    }

    private hasPermissionByKeywords(keywords: string[], actions: string[]): boolean {
        const user = this.authService.currentUser();
        const grantedPermissions = user?.permissions ?? [];
        if (grantedPermissions.length === 0) {
            return false;
        }

        const normalizedKeywords = new Set(keywords.map((item) => item.toLowerCase()));
        const normalizedActions = new Set(actions.map((item) => normalizePermissionAction(item)));

        return grantedPermissions.some((permission) => {
            const tokens = this.tokenizePermission(permission);
            if (tokens.length === 0) {
                return false;
            }

            const actionToken = normalizePermissionAction(tokens[tokens.length - 1]);
            const actionMatches = actionToken === '*' || normalizedActions.has(actionToken);
            if (!actionMatches) {
                return false;
            }

            return tokens.some((token) => normalizedKeywords.has(token) || token === '*');
        });
    }

    private tokenizePermission(permission: string): string[] {
        return permission
            .toLowerCase()
            .split(/[:._/\\-]+/)
            .map((token) => token.trim())
            .filter((token) => token.length > 0);
    }
}

